import { existsSync, readFileSync, writeFileSync, mkdirSync } from 'node:fs'
import { homedir } from 'node:os'
import { resolve, join } from 'node:path'

declare const __VERSION__: string

// Where we cache the last check. Same dir as the workspace config so we don't
// scatter state. The check runs at most once per TTL, so the network cost is
// paid roughly once a day, never on the hot path of every command.
const CACHE_DIR = join(homedir(), '.driftless')
const CACHE_FILE = join(CACHE_DIR, 'version-check.json')
const TTL_MS = 24 * 60 * 60 * 1000

const NPM_LATEST_URL = 'https://registry.npmjs.org/@driftless-sh/cli/latest'
const SKILL_URL = 'https://raw.githubusercontent.com/driftless-sh/driftless-skill/main/skills/driftless/SKILL.md'

interface VersionCache {
  checkedAt: string
  latestCli: string | null
  latestSkill: string | null
}

/** Best-effort semver compare. True when `a` is strictly behind `b`. */
export function isBehind(a: string, b: string): boolean {
  const pa = a.split('.').map((n) => parseInt(n, 10))
  const pb = b.split('.').map((n) => parseInt(n, 10))
  for (let i = 0; i < 3; i++) {
    const x = Number.isFinite(pa[i]) ? pa[i] : 0
    const y = Number.isFinite(pb[i]) ? pb[i] : 0
    if (x !== y) return x < y
  }
  return false
}

/** Pull the `version:` field out of a SKILL.md frontmatter blob. */
export function parseSkillVersion(text: string): string | null {
  const m = text.match(/^\s*version:\s*([0-9]+\.[0-9]+\.[0-9]+)/m)
  return m ? m[1] : null
}

async function fetchText(url: string, timeoutMs = 2500): Promise<string | null> {
  try {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), timeoutMs)
    const res = await fetch(url, { signal: ctrl.signal })
    clearTimeout(timer)
    if (!res.ok) return null
    return await res.text()
  } catch {
    return null
  }
}

function readCache(): VersionCache | null {
  try {
    return JSON.parse(readFileSync(CACHE_FILE, 'utf-8')) as VersionCache
  } catch {
    return null
  }
}

function writeCache(cache: VersionCache): void {
  try {
    if (!existsSync(CACHE_DIR)) mkdirSync(CACHE_DIR, { recursive: true })
    writeFileSync(CACHE_FILE, JSON.stringify(cache), 'utf-8')
  } catch {
    /* cache is best-effort — a write failure just means we re-check next time */
  }
}

/** Walk up from cwd to find the installed skill and read its version. */
function localSkillVersion(): string | null {
  let dir = process.cwd()
  for (let i = 0; i < 8; i++) {
    const p = join(dir, '.driftless', 'skill.md')
    if (existsSync(p)) {
      try {
        return parseSkillVersion(readFileSync(p, 'utf-8'))
      } catch {
        return null
      }
    }
    const parent = resolve(dir, '..')
    if (parent === dir) break
    dir = parent
  }
  return null
}

/**
 * Tell the user when their CLI or their installed skill is behind the latest —
 * the same idea as a phone app's "update available". Without it, a user can run
 * a version with a known bug (or a stale skill) forever with no signal.
 *
 * Rules: never blocks, never throws, writes to stderr, and is SKIPPED for
 * `--json` so it can't pollute the agent-facing interface. The network check
 * runs at most once per TTL (cached in ~/.driftless/version-check.json).
 */
export async function nudgeStaleVersions(isJSON: boolean): Promise<void> {
  if (isJSON) return
  try {
    let cache = readCache()
    const fresh = cache !== null && Date.now() - new Date(cache.checkedAt).getTime() < TTL_MS

    if (!fresh) {
      const [npmJson, skillText] = await Promise.all([
        fetchText(NPM_LATEST_URL),
        fetchText(SKILL_URL),
      ])
      let latestCli: string | null = null
      if (npmJson) {
        try {
          latestCli = (JSON.parse(npmJson) as { version?: string }).version ?? null
        } catch {
          /* ignore malformed registry payload */
        }
      }
      cache = {
        checkedAt: new Date().toISOString(),
        latestCli,
        latestSkill: skillText ? parseSkillVersion(skillText) : null,
      }
      writeCache(cache)
    }

    if (!cache) return

    const lines: string[] = []
    if (cache.latestCli && isBehind(__VERSION__, cache.latestCli)) {
      lines.push(`⚠ Driftless CLI ${__VERSION__} → ${cache.latestCli} available · npm i -g @driftless-sh/cli@latest`)
    }
    const localSkill = localSkillVersion()
    if (cache.latestSkill && localSkill && isBehind(localSkill, cache.latestSkill)) {
      lines.push(`⚠ Your Driftless skill is ${localSkill} (latest ${cache.latestSkill}) · run driftless install-skill`)
    }

    if (lines.length > 0) {
      for (const line of lines) console.error(line)
      console.error('')
    }
  } catch {
    /* the nudge must never break or delay the actual command */
  }
}
